import { classifyGenAIError } from '../services/genaiRetry';

export type NarrativeFailureKind =
  | 'auth-required'
  | 'auth-config'
  | 'billing-blocked'
  | 'quota-exceeded'
  | 'capacity'
  | 'schema-invalid'
  | 'empty-response'
  | 'timeout'
  | 'unknown';

export interface ClassifiedNarrativeFailure {
  kind: NarrativeFailureKind;
  userMessage: string;
}

const AUTH_REQUIRED_COPY =
  'Sign in required for AI narrative — the Gemini proxy needs a Firebase session. Sign in and click Refresh narrative.';

const AUTH_CONFIG_COPY =
  'Server missing GEMINI_API_KEY — set it in your local .env for `npm run dev` or configure Cloud Run secrets for production.';

const VERTEX_PROJECT_COPY =
  'Vertex AI project access failed (RESOURCE_PROJECT_INVALID). Restart `npm run dev` after: `gcloud auth application-default login`, `gcloud auth application-default set-quota-project datacrate-btm-b5edd`, and confirm `.env.local` has GOOGLE_CLOUD_PROJECT=datacrate-btm-b5edd and GOOGLE_CLOUD_LOCATION=us-central1.';

/*
 * Live 2026-08-19: Google put the project into billing "dunning deny" and every
 * Vertex call returned 403 `Lightning dunning decision is deny for project`.
 * That message carries none of this file's phrases, so it fell through to the
 * `classifyGenAIError` fallback, which lumps 401 and 403 together as
 * `fatal-auth` — and the fallback rendered "Sign in required". Users who were
 * already signed in were sent round a dead-end loop while every report shipped
 * as an INSUFFICIENT_DATA stub. A payment problem is neither the reader's
 * session nor a missing API key, so it gets its own kind and its own remedy.
 *
 * Copy contract: this message must RE-CLASSIFY to its own kind, because
 * `pickReportAlert` (reportAlertSelection — the report header status chip's
 * "Narrative unavailable" detail; formerly ReportAlertBanner) re-runs
 * `classifyNarrativeFailure` over the persisted `degradedReason` (which is
 * already a copy string, not the raw error) and renders the result. So it
 * deliberately contains "billing account", and
 * deliberately avoids the tokens of every branch checked before this one —
 * notably the quota phrases. (Not every copy in this file round-trips; the
 * ones this classifier owns are pinned in classifyNarrativeFailure.test.ts.)
 */
const BILLING_BLOCKED_COPY =
  'AI provider blocked: this project\'s billing account is past due or suspended, so Google is refusing every Vertex AI request. Open Cloud Console → Billing and clear the declined payment or unpaid invoice; generation resumes once it does. Signing in again will not help.';

/*
 * A 403 PERMISSION_DENIED that is NOT a billing block. `classifyGenAIError`
 * calls it `fatal-auth`, which is right as a RETRY policy (never retry) and
 * wrong as a DIAGNOSIS: the project/credential was refused, not the reader's
 * session. Round-trips via the "PERMISSION_DENIED" token in the fallback.
 */
const PROVIDER_DENIED_COPY =
  'AI provider denied this deployment: Vertex AI returned PERMISSION_DENIED (403) for this project. That is a server credential or IAM problem, not your session — check the runtime service account\'s Vertex AI role and that the API is enabled for the project.';

const EMPTY_AFTER_RETRY_COPY =
  'AI narrative empty after retry — Gemini returned no usable prose twice. Click Refresh narrative to try again.';

const EMPTY_COPY =
  'AI narrative returned empty — Gemini produced no usable prose. Click Refresh narrative to try again.';

const SCHEMA_INVALID_COPY =
  'AI narrative failed schema validation — the model response did not match the expected report shape. Click Refresh narrative to retry.';

const TIMEOUT_COPY =
  'AI narrative timed out — the Gemini call exceeded the allowed window. Try again with fewer priority documents or retry later.';

const UNKNOWN_COPY =
  'AI narrative unavailable — an unexpected error occurred during generation. Click Refresh narrative to try again.';

/**
 * Provider-side billing blocks: Google refuses the request over the PROJECT's
 * payment state. These carry no 429 and no quota tokens, so they have to be
 * matched explicitly. Checked AFTER the quota branch on purpose — a rate-limit
 * message may legitimately mention billing as the remedy ("enable billing to
 * raise your limit"), and that is still a quota problem, not a block.
 */
const BILLING_BLOCK_TOKENS = [
  // Vertex, live 2026-08-18: {"error":{"code":403,"message":"Lightning dunning
  // decision is deny for project: projects/…","status":"PERMISSION_DENIED"}}
  'dunning',
  // "The billing account for the owning project is disabled in state …"
  'billing account',
  // "This API method requires billing to be enabled. Please enable billing on
  // project #N …" — the most common phrasing, and the one a token list built
  // only from the live incident would miss.
  'requires billing',
  'enable billing',
  'billing is not enabled',
  'billing has not been enabled',
  'billing_disabled',
  'billingnotenabled',
  'consumer_suspended',
  'account has been suspended',
];

function isBillingBlocked(lower: string): boolean {
  return BILLING_BLOCK_TOKENS.some((token) => lower.includes(token));
}

/** A 401-family signal — the CALLER's session, as opposed to a 403 on the project. */
function isSessionAuthFailure(lower: string): boolean {
  return lower.includes('401')
    || lower.includes('unauthorized')
    || lower.includes('unauthenticated');
}

function classifyQuotaFromMessage(raw: string): { kind: 'aistudio-cap' | 'vertex-quota' | 'generic-429' } | null {
  const lower = raw.toLowerCase();
  const is429 = lower.includes('429') || /resource_exhausted/i.test(raw);
  if (!is429 && !lower.includes('quota exceeded') && !lower.includes('spending cap')) return null;
  if (lower.includes('monthly spending cap') || lower.includes('spending cap')) {
    return { kind: 'aistudio-cap' };
  }
  if (lower.includes('quota exceeded') || lower.includes('quota metric')) {
    return { kind: 'vertex-quota' };
  }
  return { kind: 'generic-429' };
}

function quotaUserMessage(kind: 'aistudio-cap' | 'vertex-quota' | 'generic-429', raw: string): string {
  if (kind === 'aistudio-cap') {
    return 'AI Studio spend cap exceeded. Raise the cap at https://ai.studio/spend or flip this deployment to Vertex (gcloud run services update --update-env-vars=GENAI_BACKEND=vertex).';
  }
  if (kind === 'vertex-quota') {
    return 'Vertex AI quota exceeded for this minute/region. Wait for the window to reset or request an increase via Cloud Console → APIs & Services → Quotas (model gemini-2.5-pro, online prediction requests per minute).';
  }
  return `Gemini quota error: ${raw}`;
}

function capacityCopy(raw: string): string {
  if (raw.toLowerCase().includes('at capacity')) return raw;
  return 'Datacrate AI is currently at capacity. Please try again in a few minutes.';
}

/**
 * Map a Stage-2 narrative failure (thrown error, null response, or empty
 * after retry) to an actionable user-facing message.
 */
export function classifyNarrativeFailure(input: {
  error?: string | null;
  emptyAfterRetry?: boolean;
  schemaStage?: string;
}): ClassifiedNarrativeFailure {
  const raw = (input.error ?? '').trim();
  const lower = raw.toLowerCase();

  if (!raw && input.emptyAfterRetry) {
    return { kind: 'empty-response', userMessage: EMPTY_AFTER_RETRY_COPY };
  }
  if (!raw) {
    return { kind: 'empty-response', userMessage: EMPTY_COPY };
  }

  if (
    lower.includes('authentication required') ||
    lower.includes('sign in required') ||
    lower.includes('firebase session') ||
    lower.includes('not signed in')
  ) {
    return { kind: 'auth-required', userMessage: AUTH_REQUIRED_COPY };
  }

  if (
    lower.includes('gemini_api_key') ||
    lower.includes('api key not configured on server') ||
    (lower.includes('gemini') && lower.includes('not configured')) ||
    // A present-but-rejected key is the same remedy as a missing one, and
    // naming it here keeps it out of the 403 branch below (which would blame
    // IAM for what is actually a bad key).
    lower.includes('invalid api key') ||
    lower.includes('api_key_invalid')
  ) {
    return { kind: 'auth-config', userMessage: AUTH_CONFIG_COPY };
  }

  if (/RESOURCE_PROJECT_INVALID|Invalid resource field value/i.test(raw)) {
    return { kind: 'auth-config', userMessage: VERTEX_PROJECT_COPY };
  }

  const quota = classifyQuotaFromMessage(raw);
  if (quota) {
    return { kind: 'quota-exceeded', userMessage: quotaUserMessage(quota.kind, raw) };
  }

  if (isBillingBlocked(lower)) {
    return { kind: 'billing-blocked', userMessage: BILLING_BLOCKED_COPY };
  }

  if (
    lower.includes('failed schema validation') ||
    lower.includes('gemini response failed schema') ||
    input.schemaStage
  ) {
    return { kind: 'schema-invalid', userMessage: SCHEMA_INVALID_COPY };
  }

  if (
    lower.includes('timed out') ||
    lower.includes('timeout') ||
    lower.includes('fetch failed') ||
    lower.includes('aborterror')
  ) {
    return { kind: 'timeout', userMessage: TIMEOUT_COPY };
  }

  if (
    lower.includes('at capacity') ||
    lower.includes('503') ||
    lower.includes('high demand') ||
    lower.includes('unavailable')
  ) {
    return { kind: 'capacity', userMessage: capacityCopy(raw) };
  }

  if (lower.includes('empty response') || lower.includes('returned empty')) {
    return {
      kind: 'empty-response',
      userMessage: input.emptyAfterRetry ? EMPTY_AFTER_RETRY_COPY : EMPTY_COPY,
    };
  }

  const genaiClass = classifyGenAIError(new Error(raw));
  if (genaiClass === 'fatal-auth') {
    // `classifyGenAIError` is a RETRY policy: it deliberately lumps 401 and 403
    // together as "fatal, auth family, never retry". That verdict is right for
    // retries and wrong as a diagnosis — only the 401 family means the caller
    // has no session. Returning AUTH_REQUIRED_COPY for every `fatal-auth` is
    // what told an already-signed-in operator to sign in while the project was
    // billing-blocked (live 2026-08-19). Split the two here; the retry policy
    // itself is unchanged.
    return isSessionAuthFailure(lower)
      ? { kind: 'auth-required', userMessage: AUTH_REQUIRED_COPY }
      : { kind: 'auth-config', userMessage: PROVIDER_DENIED_COPY };
  }
  if (genaiClass === 'fatal-quota') {
    const q = classifyQuotaFromMessage(raw);
    return {
      kind: 'quota-exceeded',
      userMessage: q ? quotaUserMessage(q.kind, raw) : raw,
    };
  }

  if (input.emptyAfterRetry) {
    return { kind: 'empty-response', userMessage: EMPTY_AFTER_RETRY_COPY };
  }

  return { kind: 'unknown', userMessage: raw.length > 0 ? raw : UNKNOWN_COPY };
}

/**
 * Echo the classified remedy. Callers may hand us a bare `{kind, userMessage:
 * ''}` (runTypedReportGeneration builds one from `report.degradedReason ?? ''`),
 * so an empty message must not render as a dangling "Generation incomplete — ".
 */
function toastEcho(userMessage: string): string {
  return userMessage
    ? `Generation incomplete — ${userMessage}`
    : 'Generation incomplete — AI narrative unavailable. Click Refresh narrative to try again.';
}

/**
 * Remedy copy for a DEGRADED report BODY.
 *
 * The degraded skeleton and the public-sentiment fallback bake their remedy
 * into the persisted report, which then flows to the reader, the PDF, the DOCX
 * and the PPTX — and unlike a banner it is NOT re-derived at render time. They
 * both hard-coded "Refresh narrative when AI capacity returns", which is right
 * for a capacity blip and wrong for everything else: during the 2026-08-18
 * billing block every report shipped telling the reader to wait for capacity
 * that was never the problem, and re-running could not have helped.
 *
 * Kept beside the other two kind-keyed projections (userMessage, toast) so the
 * three cannot drift; a surface that invents its own mapping is how the reader
 * and Path to GO ended up giving opposite instructions for the same state.
 */
export interface DegradedRemedyCopy {
  /** Recommendation-card title. */
  title: string;
  /** Imperative one-liner for an action / nextActions slot. */
  action: string;
  /** Fuller sentence for a description slot. */
  description: string;
}

export function degradedRemedyForKind(
  kind?: NarrativeFailureKind,
): DegradedRemedyCopy {
  switch (kind) {
    case 'billing-blocked':
      return {
        title: 'Clear the billing block, then re-run',
        action: 'Clear the billing block, then re-run this report.',
        description:
          'AI narrative generation was refused because this project\'s billing account is past due or suspended. '
          + 'Clear it in Cloud Console → Billing; re-running before it clears produces the same result.',
      };
    case 'auth-config':
      return {
        title: 'Fix the AI provider credentials, then re-run',
        action: 'Fix the AI provider credentials, then re-run this report.',
        description:
          'AI narrative generation was refused for this deployment — a server credential or IAM problem, not '
          + 'something a re-run resolves. Once the provider accepts the deployment again, re-run this report.',
      };
    case 'auth-required':
      return {
        title: 'Sign in, then re-run',
        action: 'Sign in, then re-run this report.',
        description:
          'AI narrative generation needs a signed-in session. Sign in and re-run this report.',
      };
    case 'quota-exceeded':
    case 'capacity':
      return {
        title: 'Refresh narrative when AI capacity returns',
        action: 'Refresh narrative when AI capacity returns.',
        description:
          'This report was assembled from deterministic data only because AI capacity was exhausted. '
          + 'Re-run it once capacity returns.',
      };
    default:
      return {
        title: 'Re-run to retry the AI narrative',
        action: 'Re-run this report to retry the AI narrative.',
        description:
          'This report was assembled from deterministic data only — the AI narrative pipeline did not return '
          + 'usable prose. Re-run the report to retry it.',
      };
  }
}

/** Short toast copy keyed off classified failure kind. */
export function narrativeFailureToastMessage(
  classified: ClassifiedNarrativeFailure,
): string {
  switch (classified.kind) {
    case 'auth-required':
      return 'Generation incomplete — sign in required for AI narrative. Sign in and regenerate.';
    case 'auth-config':
      // Echo rather than hard-code: `auth-config` now covers a missing key, a
      // rejected key, RESOURCE_PROJECT_INVALID and a 403 on the project, and
      // each carries its own remedy in `userMessage`.
      return toastEcho(classified.userMessage);
    case 'billing-blocked':
      return toastEcho(classified.userMessage);
    case 'quota-exceeded':
      return toastEcho(classified.userMessage);
    case 'capacity':
      return toastEcho(classified.userMessage);
    case 'empty-response':
      return 'Generation incomplete — narrative empty after retry. Click Refresh narrative to try again.';
    default:
      return 'Generation incomplete — AI narrative unavailable. Click Refresh narrative to try again.';
  }
}
